import { makeEnum, makeOptions,
  setupClass, lookupData, 
  interpolateInMap,
} from '../Base.js'

import * as psy from './Psychrometrics.js'
import * as calc from './ResidentialCalculations.js'
export { Units } from '../Common/Units.js'

import {
  FieldType,
  Field,
  FieldGroup,
} from '../Common/Field.js'
import { TypesSet } from './TypesSet.js'

import { WallType } from './WallType.js'
import { BufferSpaceType } from './BufferSpaceType.js'
import { CalcContext } from '../Common/CalcContext.js'

import { gApp, } from '../Globals.js'

/**
 * Each parititon is annotated with a 'type', which shows up in the
 * exports. Does not affect calculations.
 */
export let PartitionType = makeEnum({
  Wall: 'Wall',
  Ceiling: 'Ceiling',
  Floor: 'Floor',
})

export class Partition {
  init() {
    this.wallType = Field.makeTypeSelect('Wall Type', gApp.proj().wallTypes, null, {
      errorWhenEmpty: `You must create a Wall Type.`,
      metadata: {
        typeClass: WallType,
      }
    })
    this.size = new Field({
      name: 'Size',
      type: FieldType.Area,
      requiresInput: true,
    })
    // Required for commercial projects. Residential projects hide this field (and hidden
    // fields report no errors), using the summer/winter temperatures instead.
    this.bufferSpaceType = Field.makeTypeSelect('Buffer Space', gApp.proj().bufferSpaceTypes, null, {
      errorWhenEmpty: `You must create a Buffer Space Type`,
      metadata: {
        typeClass: BufferSpaceType,
      }
    })
    this.bufferSpaceType.setVisibility(() => !gApp.proj().isResidential());
    this.summerTemperature = new Field({
      name: 'Summer Temperature',
      type: FieldType.Temp,
      requiresInput: true,
    })
    this.winterTemperature = new Field({
      name: 'Winter Temperature',
      type: FieldType.Temp,
      requiresInput: true,
    })
    this.summerTemperature.setVisibility(() => gApp.proj().isResidential());
    this.winterTemperature.setVisibility(() => gApp.proj().isResidential());
    this.partitionType = Field.makeSelect('Type', PartitionType)

    this.serFields = [
      'wallType',
      'size',
      'bufferSpaceType',
      'summerTemperature',
      'winterTemperature',
      'partitionType',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Partition',
      path: 'partition',
    }
  }

  getWallType() {
    return this.wallType.lookupValue();
  }

  getBufferSpaceType() {
    return this.bufferSpaceType.lookupValue();
  }

  getArea() {
    return this.size.value;
  }

  getTypesSet() {
    let typesSet = TypesSet.create();
    let wallType = this.getWallType();
    typesSet.addType(wallType, 'wallTypes');
    if (wallType) {
      typesSet.addTypes(wallType.getTypesSet());
    }
    let bufferSpaceType = this.getBufferSpaceType();
    if (bufferSpaceType) {
      typesSet.addType(bufferSpaceType, 'bufferSpaceTypes');
    }
    return typesSet;
  }

  postReadFromJson(jsonData) {
    this.needsBufferTemperatureMigration = !jsonData.summerTemperature && !jsonData.winterTemperature
      && Boolean(jsonData.bufferSpaceType);
  }

  migrateBufferTemperatures() {
    if (!this.needsBufferTemperatureMigration) return;
    this.needsBufferTemperatureMigration = false;
    let bufferSpaceType = this.getBufferSpaceType();
    if (!bufferSpaceType) return;

    if (bufferSpaceType.entryMethod.value === 'Manual') {
      this.summerTemperature.setValueAsUser(
        bufferSpaceType.manualGroup.getField('summerTemperature').value);
      this.winterTemperature.setValueAsUser(
        bufferSpaceType.manualGroup.getField('winterTemperature').value);
      return;
    }

    let project = gApp.proj();
    let locationData = project.getLocationData();
    if (!locationData.isLocationSet()) return;
    let weatherData = locationData.getOutputs();
    let {heatingTemps, coolingTemps} = project.designTempInputs._getTempValues(weatherData);
    let pressure = psy.calcLocalPressure(weatherData.elevation);
    let sensibleFactor = 1.1 * pressure / psy.P_std;
    try {
      let summerContext = CalcContext.create();
      let winterContext = CalcContext.create();
      let summer = bufferSpaceType.calcTemps(summerContext, sensibleFactor,
        coolingTemps.dryBulb, project.toplevelData.indoorSummerTemp.value, false);
      let winter = bufferSpaceType.calcTemps(winterContext, sensibleFactor,
        heatingTemps.dryBulb, project.toplevelData.indoorWinterTemp.value, true);
      this.summerTemperature.setValueAsUser(summer);
      this.winterTemperature.setValueAsUser(winter);
    } catch (err) {
      console.warn(`Could not migrate partition buffer temperatures: ${err.message}`);
    }
  }

  _calcLoads(ctx, isHeating, optArgs) {
    ctx.startSection(`Partition - ${isHeating ? 'Heating' : 'Cooling'}`);

    let wallType = this.getWallType();
    let bufferSpaceType = this.getBufferSpaceType();
    ctx.assert(wallType, 'Partition is missing a Wall Type. Select a Wall Type for each partition.');
    if (!gApp.proj().isResidential()) {
      ctx.assert(bufferSpaceType,
        'Partition is missing a Buffer Space. Select a Buffer Space for each partition.');
      ctx.log(`Wall type: ${wallType.name.value}, Buffer space type: ${bufferSpaceType.name.value}`);
    }

    ctx.A = this.size.value;

    let weatherData = ctx.toplevelData.locationData;
    if (gApp.proj().isResidential()) {
      ctx.t_i = isHeating ? ctx.toplevelData.indoorWinterTemp
        : ctx.toplevelData.indoorSummerTemp;
      ctx.t_o = isHeating ? ctx.designTemps.heating : ctx.designTemps.cooling;
    } else {
      // For commercial, t_i and t_o must be given in the optArgs
      ctx.log("For commercial projects, using t_i and t_o given in optArgs...")
      ctx.assert(optArgs, 'optArgs must be given for commercial projects');
      ctx.assert(optArgs.t_i !== undefined, 'optArgs.t_i must be given for commercial projects');
      ctx.assert(optArgs.t_o !== undefined, 'optArgs.t_o must be given for commercial projects');
      ctx.t_i = optArgs.t_i;
      ctx.t_o = optArgs.t_o;
    }

    ctx.elevation = weatherData.elevation;
    ctx.P_loc = psy.calcLocalPressure(ctx.elevation);
    ctx.C_s = 1.1 * ctx.P_loc / psy.P_std;
    ctx.t_b = gApp.proj().isResidential()
      ? (isHeating ? this.winterTemperature.value : this.summerTemperature.value)
      : bufferSpaceType.calcTemps(ctx, ctx.C_s, ctx.t_o, ctx.t_i, isHeating);

    ctx.R = wallType.getRValue();
    ctx.U = ctx.eval('1.0 / R', {}, 'U');
    ctx.q = calc.calcPartitionOpaqueQ(ctx, ctx.A,
      ctx.U, ctx.t_i, ctx.t_b, isHeating);

    let q = ctx.q;
    ctx.endSection();
    return q;
  }

  calcOutputs(ctx) {
    let heatingRes = this._calcLoads(ctx, true);
    let coolingRes = this._calcLoads(ctx, false);

    let outputs = {
      q_heating: heatingRes,
      q_cooling: coolingRes,
    }
    return outputs;
  }
}
setupClass(Partition)